import { fakePromiseDelay, useAppDispatch, useAppTranslation } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import { Flex, Spin, Typography } from "@payping/web-components";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useAsyncFn } from "react-use";
import { dashboardReduxReducerActions } from "@/redux/actions/dashboard";
import { RetryGetOrderSteps } from "@/redux/reducers/dashboard";
import { PhRCheckCircle, PhRCircleNotch } from "@payping/icons";
import PayUnAvailable from "../../Unavailable";
import PayGetOrderLayout from "../Layout";

interface TypographyWithIconProps {
  type: "loading" | "done" | "disabled";
  text: string;
}
const TypographyWithIcon: FC<TypographyWithIconProps> = ({ text, type }) => {
  const { t } = useAppTranslation();
  return (
    <Typography.Paragraph disabled={type === "disabled"} className="tw:m-0!">
      <Flex align="center" gap={"small"}>
        {type === "loading" ? <Spin size="small" /> : null}
        {type === "done" ? <PhRCheckCircle className="tw:text-color-primary" width={16} /> : null}
        {type === "disabled" ? <PhRCircleNotch width={16} /> : null}
        <span>{`${text}${type === "loading" ? ` ${t("common.doing")} ...` : ""}${type === "done" ? ` ${t("common.done")}` : ""}`}</span>
      </Flex>
    </Typography.Paragraph>
  );
};

interface LoadingProps {
  goodFinish: (description: string) => void;
  badFinish: (description: string) => void;
  unknownFinish: () => void;
  trackingCode: string;
}

// creditScoreInquiry returns 400 `not_ready` while the credit report is still being generated
const REPORT_NOT_READY = "report-not-ready";
const PayGetOrderCreditScoreLoading: FC<LoadingProps> = ({ badFinish, goodFinish, trackingCode, unknownFinish }) => {
  const { t } = useAppTranslation();
  const haveInProcessRequest = useRef(false);
  const dispatch = useAppDispatch();
  const timeOutTimer = useRef(10000);

  const fakeInquiryTextsList = useMemo(
    () =>
      t("pay.getOrder.steps.creditScore.loadingInquiryFakeTexts", {
        returnObjects: true,
      }) as unknown as string[],
    [t],
  );

  // 60 * 2 * 1000 = 2 minutes
  const fakeInquiryTotalTime = 60 * 2 * 1000;
  const fakeInquiryEachTime = fakeInquiryTotalTime / fakeInquiryTextsList.length;
  const [fakeInquiryCurrent, setFakeInquiryCurrent] = useState(1);

  const [inquiryState, inquiryTry] = useAsyncFn(async () => {
    try {
      haveInProcessRequest.current = true;
      const res = await bnplConsumerApis.creditScoreInquiry({ trackingCode }, {
        isRaw: true,
      } as SDKRequestInit);
      if (res?.hasCredit === true) {
        setFakeInquiryCurrent(fakeInquiryTextsList.length + 1);
        setTimeout(() => {
          goodFinish(res.description || "ریسک متوسط");
        }, 3000);
      } else if (res?.hasCredit === false) {
        setFakeInquiryCurrent(fakeInquiryTextsList.length + 1);
        setTimeout(() => {
          badFinish(res.description || "ریسک بالا");
        }, 3000);
      }
      return res;
    } catch (e: any) {
      // Check if e is an instance of Response and if the status is 400
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          if (parsedError?.errors?.code === "not_ready") {
            // report is not ready yet — signal the retry effect below to poll again
            return REPORT_NOT_READY;
          }

          // report did not success
          if ((parsedError as any)?.key === 112) {
            await fakePromiseDelay(10000);
            setFakeInquiryCurrent(fakeInquiryTextsList.length + 1);
            setTimeout(() => {
              unknownFinish();
            }, 3000);

            return undefined;
          }
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }
      throw e;
    } finally {
      haveInProcessRequest.current = false;
    }
  }, [trackingCode, fakeInquiryTextsList.length, goodFinish, badFinish, unknownFinish]);

  useEffect(() => {
    if (!haveInProcessRequest.current) {
      inquiryTry();
    }
  }, [inquiryTry]);

  // report was not ready — retry after the current backoff, growing it each round (10s, 12.5s, ...).
  // cleanup cancels a pending retry if we unmount or the state changes in the meantime
  useEffect(() => {
    if (inquiryState.loading || inquiryState.value !== REPORT_NOT_READY) {
      return undefined;
    }
    const timer = setTimeout(() => {
      timeOutTimer.current *= 1.25;
      inquiryTry();
    }, timeOutTimer.current);
    return () => {
      clearTimeout(timer);
    };
  }, [inquiryState, inquiryTry]);

  useEffect(() => {
    // we pass all items
    if (fakeInquiryTotalTime < fakeInquiryCurrent * fakeInquiryEachTime) {
      return;
    }
    setTimeout(() => {
      setFakeInquiryCurrent((pre) => pre + 1);
    }, fakeInquiryEachTime);
  }, [fakeInquiryCurrent, fakeInquiryEachTime, fakeInquiryTotalTime]);

  if (inquiryState.error) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
        />
      </PayGetOrderLayout>
    );
  }

  return (
    <PayGetOrderLayout showHeaders>
      <Flex vertical className="tw:h-full tw:w-full tw:px-4! tw:pb-4! tw:pt-6!" gap={"large"}>
        <Flex vertical gap={"small"}>
          <Typography.Title level={5} className="tw:m-0! tw:font-semibold!">
            {t("pay.getOrder.steps.creditScore.loadingCreditTitle")}
          </Typography.Title>
          <Typography.Paragraph className="tw:m-0!">
            <div>{t("pay.getOrder.steps.creditScore.loadingCreditContent")}</div>
            <div>{t("pay.getOrder.steps.creditScore.loadingCreditSubContent")}</div>
          </Typography.Paragraph>
        </Flex>
        <Flex vertical gap="small" className="tw:w-full">
          {fakeInquiryTextsList.map((item, index) => {
            if (index + 1 > fakeInquiryCurrent) {
              return <TypographyWithIcon text={item} type="disabled" key={index} />;
            }
            if (index + 1 === fakeInquiryCurrent) {
              return <TypographyWithIcon text={item} type="loading" key={index} />;
            }
            return <TypographyWithIcon text={item} type="done" key={index} />;
          })}
        </Flex>
      </Flex>
    </PayGetOrderLayout>
  );
};

export default PayGetOrderCreditScoreLoading;
